import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Container, Table, Modal } from 'react-bootstrap';
import { BsDownload } from 'react-icons/bs';
import { GetCanPacketHealthSummary, GetCanPacketHealthDetails } from '../../../store/actions/reports/can-packet-health';
import { GetSubscribedVehiclesByCIAction } from '../../../store/actions/vehicle/vehicle';
import Pagination from '../../helpers/hoc/paginator/Pagination';
import MultiSelect from '../../helpers/hoc/mult-select/MultiSelect';
import Loader from '../../helpers/hoc/loader/Loader';
import NoRecordFound from '../../helpers/hoc/noRecordFound';
import { mapper } from '../../../utils/mapper';
import { fileDownloader } from '../../../utils/common-methods';
import DatePicker from '../../helpers/hoc/date-picker/DatePicker';
import * as ExcelJS from 'exceljs/dist/exceljs.min.js';
import './CanPacketHealth.css';

const PAGE_SIZE = 10;

const formatDate = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return formatDate(d);
};

const anomalyCategoryOptions = [
  { label: 'All Categories', value: 'all' },
  { label: 'Odometer Spikes', value: 'odometerSpikes' },
  { label: 'SOC Anomalies', value: 'socAnomalies' },
  { label: 'Voltage Anomalies', value: 'voltageAnomalies' },
  { label: 'Temperature', value: 'temperatureAnomalies' },
  { label: 'Junk Packets', value: 'junkPackets' },
  { label: 'Ping Gaps', value: 'pingGaps' },
];

const summaryCards = [
  { key: 'odometerSpikes', label: 'Odometer Spikes', color: '#e74c3c' },
  { key: 'socAnomalies', label: 'SOC Anomalies', color: '#e67e22' },
  { key: 'voltageAnomalies', label: 'Voltage Anomalies', color: '#9b59b6' },
  { key: 'temperatureAnomalies', label: 'Temperature', color: '#e84393' },
  { key: 'junkPackets', label: 'Junk Packets', color: '#d63031' },
  { key: 'pingGaps', label: 'Ping Gaps', color: '#636e72' },
];

const issueTypeLabels = {
  JUNK_OD: 'Junk Odometer',
  JUNK_SOC: 'Junk SOC',
  SOC_OUT_RANGE: 'SOC Out of Range',
  OD_SPIKE_UP: 'OD Spike Up',
  OD_SPIKE_DN: 'OD Spike Down',
  SOC_JUMP_UP: 'SOC Jump Up',
  SOC_DROP_HIGH: 'SOC Drop High',
  VBV_SPIKE: 'Voltage Spike',
  TEMP_BAT_HIGH: 'Battery Temp High',
  TEMP_CTRL_HIGH: 'Controller Temp High',
  TEMP_MOT_HIGH: 'Motor Temp High',
  PING_GAP: 'Ping Gap',
};

const CanPacketHealth = () => {
  const dispatch = useDispatch();
  const summary = useSelector((state) => state.canPacketHealth?.summary);
  const details = useSelector((state) => state.canPacketHealth?.details);
  const subscribedVehicles = useSelector((state) => state.vehicle?.subscribedVehiclesByCI);

  const [selectedDate, setSelectedDate] = useState(yesterday());
  const [clearDateSelection, setClearDateSelection] = useState(false);
  const [selectedCity, setSelectedCity] = useState([]);
  const [selectedOemModel, setSelectedOemModel] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const mappedOptions = mapper.mapVehicleDataOptions(subscribedVehicles || []);

  const onDateChange = (dates) => {
    if (dates && dates[0]) {
      setSelectedDate(formatDate(dates[0]));
      setClearDateSelection(false);
    }
  };

  useEffect(() => {
    if (!subscribedVehicles?.length) {
      dispatch(GetSubscribedVehiclesByCIAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSummary = async () => {
    setLoading(true);
    await dispatch(GetCanPacketHealthSummary({
      date: selectedDate,
      city: selectedCity?.map((x) => x.value),
      oemAndModels: selectedOemModel?.map((x) => x.value),
    }));
    setLoading(false);
  };

  const handleFetch = () => {
    setCurrentPage(1);
    fetchSummary();
  };

  const handleViewDetails = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetailModal(true);
    setDetailLoading(true);
    await dispatch(GetCanPacketHealthDetails({
      registrationNumber: vehicle.registrationNumber,
      date: selectedDate,
    }));
    setDetailLoading(false);
  };

  // Client-side category filter
  const filteredVehicles = useMemo(() => {
    if (!summary?.vehicles) return [];
    if (!selectedCategory.length || selectedCategory.some(c => c.value === 'all')) {
      return summary.vehicles;
    }
    return summary.vehicles.filter(v =>
      selectedCategory.some(c => v[c.value] > 0)
    );
  }, [summary?.vehicles, selectedCategory]);

  // Client-side pagination
  const totalPages = Math.ceil(filteredVehicles.length / PAGE_SIZE);
  const paginatedVehicles = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredVehicles.slice(start, start + PAGE_SIZE);
  }, [filteredVehicles, currentPage]);

  // Reset page when category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  const formatTimestamp = (dts) => {
    if (!dts) return '-';
    const d = new Date(dts);
    const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
    return ist.toISOString().replace('T', ' ').substring(0, 19);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!filteredVehicles.length) return;

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('CAN Packet Health');

    sheet.columns = [
      { header: 'S.No', key: 'sno', width: 6 },
      { header: 'Registration Number', key: 'registrationNumber', width: 22 },
      { header: 'OEM', key: 'oem', width: 12 },
      { header: 'Model', key: 'model', width: 14 },
      { header: 'City', key: 'city', width: 12 },
      { header: 'Total Pings', key: 'totalPings', width: 12 },
      { header: 'OD Spikes', key: 'odometerSpikes', width: 12 },
      { header: 'SOC Anomalies', key: 'socAnomalies', width: 14 },
      { header: 'Voltage Anomalies', key: 'voltageAnomalies', width: 16 },
      { header: 'Temperature', key: 'temperatureAnomalies', width: 14 },
      { header: 'Junk Packets', key: 'junkPackets', width: 13 },
      { header: 'Ping Gaps', key: 'pingGaps', width: 11 },
      { header: 'Total Anomalies', key: 'totalAnomalies', width: 15 },
    ];

    const headerRow = sheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF10204B' } };
    headerRow.alignment = { horizontal: 'center' };

    filteredVehicles.forEach((v, idx) => {
      sheet.addRow({
        sno: idx + 1,
        registrationNumber: v.registrationNumber,
        oem: v.oem || '-',
        model: v.model || '-',
        city: v.city || '-',
        totalPings: v.totalPings,
        odometerSpikes: v.odometerSpikes,
        socAnomalies: v.socAnomalies,
        voltageAnomalies: v.voltageAnomalies,
        temperatureAnomalies: v.temperatureAnomalies,
        junkPackets: v.junkPackets,
        pingGaps: v.pingGaps,
        totalAnomalies: v.totalAnomalies,
      });
    });

    sheet.addRow({});
    const infoRow = sheet.addRow({
      sno: '',
      registrationNumber: `Date: ${selectedDate}`,
      oem: `Scanned: ${summary?.totalVehiclesScanned || 0}`,
      model: `Anomalous: ${filteredVehicles.length}`,
    });
    infoRow.font = { bold: true };

    const categoryLabel = selectedCategory.length && !selectedCategory.some(c => c.value === 'all')
      ? selectedCategory.map(c => c.label).join(', ')
      : 'All';

    fileDownloader({
      excelWorkbook: workbook,
      fileName: `CAN_Packet_Health_${selectedDate}_${categoryLabel.replace(/\s/g, '_')}.xlsx`,
    });
  };

  return (
    <>
      <Container fluid className="cph-container">
        {/* Summary Cards */}
        {summary && (
          <div className="cph-summary-row">
            <div className="cph-summary-card cph-card-info">
              <div className="cph-card-value">{summary.totalVehiclesScanned}</div>
              <div className="cph-card-label">Vehicles Scanned</div>
            </div>
            <div className="cph-summary-card cph-card-warning">
              <div className="cph-card-value">{summary.totalAnomalousVehicles}</div>
              <div className="cph-card-label">Anomalous Vehicles</div>
            </div>
            {summaryCards.map((card) => (
              <div key={card.key} className="cph-summary-card" style={{ borderTop: `3px solid ${card.color}` }}>
                <div className="cph-card-value">{summary.vehicleCounts?.[card.key] || 0}</div>
                <div className="cph-card-sub">{summary.anomalyCounts?.[card.key] || 0} events</div>
                <div className="cph-card-label">{card.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filter Bar — same layout as /reports/historical */}
        <div className='row d-flex tripLogsFilter'>
          <div className={`col-2 multi-select-width ${!selectedDate ? 'no-value-selected' : ''}`}>
            <DatePicker
              onDateChange={onDateChange}
              allowedMaxDaysInSelection={1}
              paddingLeft='0px'
              clearDateSelectionOnToggle={clearDateSelection}
              placeholderDefaultText='Select Date'
              isShowOneCalendar={true}
              dateFormat='dd-MMM-yyyy'
              isTodayDisable={true}
              disableBeforeDays={7}
            />
          </div>

          <div className={`col-2 multi-select-width ${!selectedCity[0] ? 'no-value-selected' : ''}`}>
            <MultiSelect
              options={mappedOptions.location}
              selectedOptions={selectedCity}
              placeholder='City'
              setSelectedOptions={setSelectedCity}
              isMultiSelect={true}
              customBackgroundColor='#EFF3F7'
              webkitScrollbarWidth='5px'
              webkitScrollbarHeight='5px'
              minHeight='45px'
              placeholderAlignment='flex-start'
              indicatorSeparatorDisplay='none'
              placeholderColor='#C6C6C6'
              valueContainerColor='#797694'
              optionPosition='justify-content-start'
              valueContainerWidthInPx='200px'
              valueContainerMaxWidthInPx='200px'
              menuListPaddingX='px-2'
              okButtonPaddingEnd='pe-2'
            />
          </div>

          <div className={`col-2 multi-select-width ${!selectedOemModel[0] ? 'no-value-selected' : ''}`}>
            <MultiSelect
              options={mappedOptions.oemModel}
              selectedOptions={selectedOemModel}
              placeholder='OEM & Model'
              setSelectedOptions={setSelectedOemModel}
              isMultiSelect={true}
              customBackgroundColor='#EFF3F7'
              webkitScrollbarWidth='5px'
              webkitScrollbarHeight='5px'
              minHeight='45px'
              placeholderAlignment='flex-start'
              indicatorSeparatorDisplay='none'
              placeholderColor='#C6C6C6'
              valueContainerColor='#797694'
              optionPosition='justify-content-start'
              valueContainerWidthInPx='200px'
              valueContainerMaxWidthInPx='200px'
              menuListPaddingX='px-2'
              okButtonPaddingEnd='pe-2'
            />
          </div>

          <div className={`col-2 multi-select-width ${!selectedCategory[0] ? 'no-value-selected' : ''}`}>
            <MultiSelect
              options={anomalyCategoryOptions}
              selectedOptions={selectedCategory}
              placeholder='Anomaly Category'
              setSelectedOptions={setSelectedCategory}
              isMultiSelect={true}
              customBackgroundColor='#EFF3F7'
              webkitScrollbarWidth='5px'
              webkitScrollbarHeight='5px'
              minHeight='45px'
              placeholderAlignment='flex-start'
              indicatorSeparatorDisplay='none'
              placeholderColor='#C6C6C6'
              valueContainerColor='#797694'
              optionPosition='justify-content-start'
              valueContainerWidthInPx='200px'
              valueContainerMaxWidthInPx='200px'
              menuListPaddingX='px-2'
              okButtonPaddingEnd='pe-2'
            />
          </div>

          <div className='col-1'>
            <Button
              className='bttn height'
              onClick={handleFetch}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Report'}
            </Button>
          </div>

          <div className='col-1 export-btn-box'>
            <button
              disabled={!filteredVehicles?.length}
              className={!filteredVehicles?.length ? 'dwn-btn dwn-btn-disabled' : 'dwn-btn'}
              onClick={exportToExcel}
            >
              <BsDownload style={{ width: '22px', height: '22px' }} />
            </button>
          </div>
        </div>

        {/* Vehicle Table */}
        {filteredVehicles?.length ? (
          <div className='cph-table-wrapper'>
            <div className="cph-table-header">
              <span className="cph-table-title">
                Anomalous Vehicles ({filteredVehicles.length})
              </span>
            </div>
            <div className="cph-table-scroll">
              <Table
                borderless
                className='mb-0 cph-data-table'
                id='cph-table'
              >
                <thead>
                  <tr className='th-border-bottom'>
                    <th className='ps-2 text-nowrap'><p className='fw500'>S.No</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>Reg. Number</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>OEM</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>Model</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>City</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>Total Pings</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>OD Spikes</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>SOC</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>Voltage</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>Temp</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>Junk</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>Gaps</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>Total</p></th>
                    <th className='ps-2 text-nowrap'><p className='fw500'>Actions</p></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedVehicles?.map((v, index) => (
                    <tr key={index}>
                      <td className='ps-2'>{(currentPage - 1) * PAGE_SIZE + index + 1}.</td>
                      <td>{v.registrationNumber}</td>
                      <td>{v.oem || '-'}</td>
                      <td>{v.model || '-'}</td>
                      <td>{v.city || '-'}</td>
                      <td>{v.totalPings}</td>
                      <td>{v.odometerSpikes}</td>
                      <td>{v.socAnomalies}</td>
                      <td>{v.voltageAnomalies}</td>
                      <td>{v.temperatureAnomalies}</td>
                      <td>{v.junkPackets}</td>
                      <td>{v.pingGaps}</td>
                      <td><strong>{v.totalAnomalies}</strong></td>
                      <td>
                        <button
                          className='cph-btn-details'
                          onClick={() => handleViewDetails(v)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <Pagination
              disabled={false}
              totalPages={totalPages}
              currentPage={currentPage}
              setPage={setCurrentPage}
            />
          </div>
        ) : (
          <></>
        )}
        {!filteredVehicles?.length && !loading && summary ? (
          <NoRecordFound />
        ) : (
          <></>
        )}
      </Container>
      <Loader isLoading={loading} />

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title className="cph-modal-title">
            Anomaly Details - {selectedVehicle?.registrationNumber}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="cph-loading">Loading details...</div>
          ) : details ? (
            <div className="cph-detail-content">
              <div className="cph-detail-info">
                <div className="cph-detail-stat">
                  <span className="cph-detail-stat-label">Total Pings</span>
                  <span className="cph-detail-stat-value">{details.totalPings}</span>
                </div>
                <div className="cph-detail-stat">
                  <span className="cph-detail-stat-label">Anomalous Records</span>
                  <span className="cph-detail-stat-value">{details.anomalousRecords?.length || 0}</span>
                </div>
                <div className="cph-detail-stat">
                  <span className="cph-detail-stat-label">Date</span>
                  <span className="cph-detail-stat-value">{details.date}</span>
                </div>
              </div>
              <div className="cph-detail-table-wrap">
                <table className="table table-sm table-bordered cph-detail-table">
                  <thead>
                    <tr>
                      <th>Timestamp (IST)</th>
                      <th>OD</th>
                      <th>SOC</th>
                      <th>Voltage</th>
                      <th>Bat Temp</th>
                      <th>Ctrl Temp</th>
                      <th>Motor Temp</th>
                      <th>Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.anomalousRecords?.map((rec, idx) => (
                      <tr key={idx}>
                        <td>{formatTimestamp(rec.dts)}</td>
                        <td>{rec.od ?? '-'}</td>
                        <td>{rec.soc ?? '-'}</td>
                        <td>{rec.vbv ?? '-'}</td>
                        <td>{rec.vbt ?? '-'}</td>
                        <td>{rec.vct ?? '-'}</td>
                        <td>{rec.vmt ?? '-'}</td>
                        <td>
                          {rec.issues?.map((issue, i) => (
                            <span key={i} className="cph-issue-badge">
                              {issueTypeLabels[issue.type] || issue.type}
                              {issue.delta !== undefined && ` (${issue.delta})`}
                              {issue.gapMinutes !== undefined && ` (${issue.gapMinutes}min)`}
                            </span>
                          ))}
                        </td>
                      </tr>
                    ))}
                    {(!details.anomalousRecords || details.anomalousRecords.length === 0) && (
                      <tr><td colSpan={8} className="text-center">No anomalies found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="cph-loading">No data</div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CanPacketHealth;
